<template>
  <main
    class="predix-public-report min-h-screen w-full"
    style="font-family: 'Inter', sans-serif;"
  >
    <div class="predix-public-report__bg" aria-hidden="true" />

    <div class="predix-public-report__inner">
      <div v-if="loading" class="predix-public-report__state">
        <div class="predix-public-report__spinner" />
        <p>Loading report…</p>
      </div>

      <div
        v-else-if="needsPassword"
        class="predix-public-report__unlock"
      >
        <div class="predix-public-report__unlock-icon" aria-hidden="true">
          <i class="fa-solid fa-lock" />
        </div>
        <h2 class="predix-public-report__unlock-title">Password required</h2>
        <p class="predix-public-report__unlock-text">
          This report is protected. Enter the password shared with you to view it.
        </p>
        <form class="predix-public-report__unlock-form" @submit.prevent="submitPassword">
          <label class="predix-public-report__unlock-label" for="report-unlock-password">
            Password
          </label>
          <input
            id="report-unlock-password"
            ref="passwordField"
            v-model="passwordInput"
            class="predix-public-report__unlock-input"
            :class="{ 'predix-public-report__unlock-input--error': !!passwordError }"
            type="password"
            name="password"
            autocomplete="current-password"
            placeholder="Enter report password"
            :disabled="unlocking"
          />
          <p v-if="passwordError" class="predix-public-report__unlock-error">
            {{ passwordError }}
          </p>
          <button
            type="submit"
            class="predix-public-report__unlock-btn"
            :disabled="!passwordInput.trim() || unlocking"
          >
            {{ unlocking ? 'Checking…' : 'Unlock report' }}
          </button>
        </form>
      </div>

      <div v-else-if="error" class="predix-public-report__state predix-public-report__state--error">
        <i class="fa-regular fa-circle-xmark predix-public-report__state-icon" aria-hidden="true" />
        <p>{{ error }}</p>
      </div>

      <div v-else class="predix-public-report__layout">
        <aside class="predix-public-report__sidebar">
          <header class="predix-public-report__header">
            <div class="predix-public-report__brand">
              <img
                class="predix-public-report__logo"
                :class="{ 'predix-public-report__logo--ega': useEgaLogo }"
                :src="reportLogoSrc"
                alt="MetalNx"
              />
              <div class="predix-public-report__brand-text">
                <h1 class="predix-public-report__title">{{ heading }}</h1>
                <div class="predix-public-report__eyebrow">{{ reportEyebrow }}</div>
              </div>
            </div>
          </header>

          <section
            v-if="compositionGroup"
            class="predix-public-report__cat predix-public-report__cat--composition"
          >
            <div class="predix-public-report__cat-head">
              <h2 class="predix-public-report__cat-title">{{ compositionLabel }}</h2>
            </div>
            <div class="predix-public-report__table-wrap">
              <table class="predix-public-report__table">
                <thead>
                  <tr>
                    <th>{{ compositionLabel }}</th>
                    <th class="predix-public-report__col-value">Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, index) in compositionGroup.items"
                    :key="`composition-${index}-${itemKey(item)}`"
                  >
                    <td>{{ itemKey(item) }}</td>
                    <td
                      class="predix-public-report__col-value predix-public-report__col-value--copyable"
                      :class="{ 'is-copied': copiedValueKey === `composition-${index}` }"
                      :title="copiedValueKey === `composition-${index}` ? 'Copied' : 'Click to copy'"
                      @click="copyPredictedValue(item.value, `composition-${index}`, item)"
                    >
                      {{ displayCompositionValue(item) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <button
            type="button"
            class="predix-public-report__pdf-btn"
            :disabled="pdfDownloading || !items.length"
            @click="downloadPdf"
          >
            <i
              class="fa-solid"
              :class="pdfDownloading ? 'fa-spinner fa-spin' : 'fa-file-pdf'"
              aria-hidden="true"
            />
            {{ pdfDownloading ? 'Preparing…' : 'Download as PDF' }}
          </button>
        </aside>

        <div class="predix-public-report__content">
          <div
            v-if="!items.length"
            class="predix-public-report__state"
          >
            <i class="fa-regular fa-folder-open predix-public-report__state-icon" aria-hidden="true" />
            <p>This report has no items.</p>
          </div>

          <section
            v-for="group in otherGroups"
            :key="group.cat"
            class="predix-public-report__cat"
          >
            <div class="predix-public-report__cat-head">
              <h2 class="predix-public-report__cat-title">{{ displayCatTitle(group.cat) }}</h2>
            </div>

            <div class="predix-public-report__table-wrap">
              <table class="predix-public-report__table">
                <thead>
                  <tr>
                    <th>{{ displayCat(group.cat) }}</th>
                    <th class="predix-public-report__col-value">Predicted Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, index) in group.items"
                    :key="`${group.cat}-${index}-${itemKey(item)}`"
                  >
                    <td>
                      <div class="predix-public-report__prop">
                        <span class="predix-public-report__prop-label">{{ itemKey(item) }}</span>
                        <div
                          v-if="hasPropertyHelp(itemKey(item))"
                          class="predix-public-report__help"
                          @click.stop.prevent
                          @mousedown.stop.prevent
                          @mouseenter="onHelpEnter($event, itemKey(item))"
                          @mousemove="onHelpMove($event)"
                          @mouseleave="onHelpLeave"
                          @focusin="onHelpEnter($event, itemKey(item))"
                          @focusout="onHelpLeave"
                        >
                          <button
                            type="button"
                            class="predix-public-report__help-btn"
                            :aria-label="'About ' + itemKey(item)"
                            :aria-describedby="hoveredHelpPropertyName === itemKey(item) ? 'report-property-help-tip' : undefined"
                          >
                            ?
                          </button>
                        </div>
                      </div>
                    </td>
                    <td
                      class="predix-public-report__col-value predix-public-report__col-value--copyable"
                      :class="{ 'is-copied': copiedValueKey === `${group.cat}-${index}` }"
                      :title="copiedValueKey === `${group.cat}-${index}` ? 'Copied' : 'Click to copy'"
                      @click="copyPredictedValue(item.value, `${group.cat}-${index}`)"
                    >
                      {{ displayValue(item.value) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>

    <div
      v-show="hoveredHelpContent"
      id="report-property-help-tip"
      class="predix-public-report__help-tip"
      role="tooltip"
      :style="helpTipStyle"
    >
      <div class="predix-public-report__help-tip-title">
        {{ hoveredHelpContent && hoveredHelpContent.title }}
      </div>
      <p class="predix-public-report__help-tip-body">
        {{ hoveredHelpContent && hoveredHelpContent.description }}
      </p>
    </div>
  </main>
</template>

<script>
import { getReport } from '@/api/reports'
import { smartPruneNumber } from '@/utils/number'
import { isAbdullaUser, platformLogoForUser } from '@/utils/platformLogo'
import { downloadPredixReportPdf } from '@/utils/predixReportPdf'
import castingPropertyHelpMainDb from '@/constants/castingPropertyHelp.mainDb.json'
import castingPropertyHelpBenchmarkDb from '@/constants/castingPropertyHelp.benchmarkDb.json'
import { hasCastingPropertyHelp, resolveCastingPropertyHelp } from '@/utils/castingPropertyHelp'

const COMPOSITION_LABEL = 'Alloy Composition'
const COMPOSITION_CAT_ALIASES = new Set(['Composition', 'Alloy Composition'])
const PASSWORD_STORAGE_PREFIX = 'predix-report-pw:'

export default {
  name: 'PredixPublicReport',
  data: () => ({
    loading: true,
    unlocking: false,
    pdfDownloading: false,
    error: '',
    needsPassword: false,
    passwordInput: '',
    passwordError: '',
    unlockedPassword: '',
    reportName: '',
    createdBy: '',
    items: [],
    copiedValueKey: '',
    hoveredHelpPropertyName: null,
    hoveredHelpContent: null,
    helpTipStyle: {
      top: '0px',
      left: '0px',
    },
  }),
  computed: {
    castingPropertyHelpMap() {
      return {
        ...castingPropertyHelpBenchmarkDb,
        ...castingPropertyHelpMainDb,
      }
    },
    reportId() {
      return this.$route.params.id
    },
    heading() {
      if (this.loading) return '…'
      if (this.needsPassword) return 'Protected report'
      if (this.error) return 'Report unavailable'
      return this.reportName || 'Untitled report'
    },
    compositionLabel() {
      return COMPOSITION_LABEL
    },
    reportEyebrow() {
      if (this.useEgaLogo) {
        return 'Generated by MetalNx PrediX™ and the EGA Database'
      }
      return "Generated by MetalNx's PrediX™"
    },
    useEgaLogo() {
      return isAbdullaUser(this.createdBy)
    },
    reportLogoSrc() {
      return platformLogoForUser(this.createdBy, { context: 'report' })
    },
    groupedCategories() {
      const order = []
      const map = {}
      for (const item of this.items) {
        const cat = this.itemCat(item)
        if (!map[cat]) {
          map[cat] = []
          order.push(cat)
        }
        map[cat].push(item)
      }
      order.sort((a, b) => {
        const aComp = this.isCompositionCat(a)
        const bComp = this.isCompositionCat(b)
        if (aComp && !bComp) return -1
        if (bComp && !aComp) return 1
        return 0
      })
      return order.map((cat) => {
        const items = map[cat]
        if (this.isCompositionCat(cat)) {
          return { cat, items: this.withBaseAlRow(items) }
        }
        return { cat, items }
      })
    },
    compositionGroup() {
      return this.groupedCategories.find((g) => this.isCompositionCat(g.cat)) || null
    },
    otherGroups() {
      return this.groupedCategories.filter((g) => !this.isCompositionCat(g.cat))
    },
  },
  watch: {
    reportId: {
      immediate: true,
      handler(id) {
        this.fetchReport(id)
      },
    },
  },
  methods: {
    isCompositionCat(cat) {
      return COMPOSITION_CAT_ALIASES.has(String(cat || '').trim())
    },
    displayCat(cat) {
      if (this.isCompositionCat(cat)) return COMPOSITION_LABEL
      const name = String(cat || '').trim() || 'Other'
      if (/^alloy\b/i.test(name)) return name
      return `Alloy ${name}`
    },
    displayCatTitle(cat) {
      return this.displayCat(cat).replace(/^alloy\s+/i, '').trim() || this.displayCat(cat)
    },
    itemCat(item) {
      const raw = item && (item.cat || item.category || item.group)
      const cat = raw != null && String(raw).trim() ? String(raw).trim() : 'Other'
      return this.displayCat(cat)
    },
    itemKey(item) {
      return (item && (item.key || item.name || item.title)) || '—'
    },
    isAluminumName(name) {
      const s = String(name || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ')
      return s === 'al' || s === 'al wt%' || s === 'al wt' || s === 'aluminum' || s === 'aluminium'
    },
    withBaseAlRow(items = []) {
      const compositionItems = Array.isArray(items) ? items : []

      let sumOther = 0
      let hasOther = false
      const normalized = []

      compositionItems.forEach((it) => {
        const name = this.itemKey(it)
        if (this.isAluminumName(name)) return
        const n = typeof it.value === 'number' ? it.value : parseFloat(it && it.value)
        if (Number.isFinite(n)) {
          sumOther += n
          hasOther = true
        }
        normalized.push(it)
      })

      const alValue = hasOther ? Number((100 - sumOther).toFixed(3)) : 0

      return [
        { key: 'Al wt%', name: 'Al wt%', value: alValue, title: 'Al wt%' },
        ...normalized,
      ]
    },
    displayCompositionValue(item) {
      const n = typeof item.value === 'number' ? item.value : parseFloat(item && item.value)
      if (!Number.isFinite(n)) return item && item.value == null ? '—' : String(item && item.value)
      if (this.isAluminumName(this.itemKey(item))) {
        return Number(n.toFixed(3)).toString()
      }
      return this.displayValue(n)
    },
    hasPropertyHelp(propertyName = '') {
      return hasCastingPropertyHelp(this.castingPropertyHelpMap, propertyName)
    },
    propertyHelp(propertyName = '') {
      return resolveCastingPropertyHelp(this.castingPropertyHelpMap, propertyName)
    },
    onHelpEnter(event, propertyName) {
      const help = this.propertyHelp(propertyName)
      if (!help) {
        this.onHelpLeave()
        return
      }
      this.hoveredHelpPropertyName = propertyName
      this.hoveredHelpContent = help
      this.updateHelpTipPosition(event)
    },
    onHelpMove(event) {
      if (this.hoveredHelpPropertyName == null) return
      this.updateHelpTipPosition(event)
    },
    onHelpLeave() {
      this.hoveredHelpPropertyName = null
      this.hoveredHelpContent = null
    },
    updateHelpTipPosition(event) {
      const offsetX = 14
      const x = event && Number.isFinite(event.clientX) ? event.clientX + offsetX : 0
      const y = event && Number.isFinite(event.clientY) ? event.clientY : 0
      this.helpTipStyle = {
        top: `${y}px`,
        left: `${x}px`,
      }
    },
    displayValue(value) {
      const n = typeof value === 'number' ? value : parseFloat(value)
      if (Number.isFinite(n)) return smartPruneNumber(n)
      return value == null ? '—' : String(value)
    },
    async copyPredictedValue(value, key, item) {
      const text = String(item ? this.displayCompositionValue(item) : this.displayValue(value))
      if (!text || text === '—') return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          const ta = document.createElement('textarea')
          ta.value = text
          ta.setAttribute('readonly', '')
          ta.style.position = 'fixed'
          ta.style.left = '-9999px'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.copiedValueKey = key
        clearTimeout(this._copiedValueTimer)
        this._copiedValueTimer = setTimeout(() => {
          if (this.copiedValueKey === key) this.copiedValueKey = ''
        }, 1500)
      } catch (e) {
        /* ignore */
      }
    },
    async downloadPdf() {
      if (this.pdfDownloading || !this.items.length) return
      this.pdfDownloading = true
      try {
        await downloadPredixReportPdf({
          title: this.reportName || 'Untitled report',
          reportId: this.reportId,
          groups: this.groupedCategories.map((g) => ({
            ...g,
            cat: this.displayCat(g.cat),
          })),
          eyebrow: this.reportEyebrow,
          useEgaLogo: this.useEgaLogo,
          logoSrc: platformLogoForUser(this.createdBy, { context: 'pdf' }),
        })
      } catch (e) {
        if (this.$root && this.$root.notify) {
          this.$root.notify.show({ message: 'Could not create PDF.', type: 'error' })
        }
      } finally {
        this.pdfDownloading = false
      }
    },
    extractCreatedBy(res) {
      if (!res || typeof res !== 'object') return ''
      const raw =
        res.createdBy ||
        res.created_by ||
        res.author ||
        res.owner ||
        res.username ||
        (res.user && (res.user.username || res.user.name || res.user)) ||
        ''
      return typeof raw === 'string' ? raw : String(raw || '')
    },
    passwordStorageKey(id) {
      return `${PASSWORD_STORAGE_PREFIX}${id}`
    },
    readStoredPassword(id) {
      if (!id) return ''
      try {
        return sessionStorage.getItem(this.passwordStorageKey(id)) || ''
      } catch (e) {
        return ''
      }
    },
    storePassword(id, password) {
      if (!id || !password) return
      try {
        sessionStorage.setItem(this.passwordStorageKey(id), password)
      } catch (e) {
        /* ignore quota / private mode */
      }
    },
    clearStoredPassword(id) {
      if (!id) return
      try {
        sessionStorage.removeItem(this.passwordStorageKey(id))
      } catch (e) {
        /* ignore */
      }
    },
    isPasswordRequiredError(err) {
      if (!err || err.status !== 403) return false
      const data = err.data || {}
      if (data.hasPassword === true || data.has_password === true) return true
      const detail = String(data.detail || data.message || '').toLowerCase()
      return detail.includes('password')
    },
    applyReport(res) {
      this.reportName = (res && res.name) || ''
      this.createdBy = this.extractCreatedBy(res)
      const rawItems = (res && (res.items || res.data || res.results)) || []
      this.items = Array.isArray(rawItems) ? rawItems : []
      this.needsPassword = false
      this.passwordError = ''
      this.error = ''
    },
    fetchReport(id, options = {}) {
      const { password = '', fromUnlock = false } = options
      if (!fromUnlock) {
        this.loading = true
        this.error = ''
        this.needsPassword = false
        this.passwordInput = ''
        this.passwordError = ''
        this.unlockedPassword = ''
        this.reportName = ''
        this.createdBy = ''
        this.items = []
      } else {
        this.unlocking = true
        this.passwordError = ''
      }

      if (!id) {
        this.loading = false
        this.unlocking = false
        this.error = 'Missing report id.'
        return
      }

      const attemptPassword = password || this.readStoredPassword(id)
      getReport(
        id,
        (res) => {
          this.loading = false
          this.unlocking = false
          this.applyReport(res)
          if (attemptPassword) {
            this.unlockedPassword = attemptPassword
            this.storePassword(id, attemptPassword)
          } else {
            this.clearStoredPassword(id)
          }
        },
        (err) => {
          this.loading = false
          this.unlocking = false
          const status = err && err.status
          if (this.isPasswordRequiredError(err)) {
            // Wrong/missing stored password — clear and ask again
            if (attemptPassword && !fromUnlock) {
              this.clearStoredPassword(id)
            }
            this.needsPassword = true
            this.error = ''
            this.items = []
            if (fromUnlock) {
              this.passwordError = 'Incorrect password. Please try again.'
            }
            this.$nextTick(() => {
              if (this.$refs.passwordField) this.$refs.passwordField.focus()
            })
            return
          }
          this.needsPassword = false
          if (status === 404) {
            this.error = 'Report not found.'
          } else {
            this.error = 'Could not load this report. Please try again later.'
          }
        },
        attemptPassword ? { password: attemptPassword } : {}
      )
    },
    submitPassword() {
      const password = this.passwordInput.trim()
      if (!password || this.unlocking) return
      this.fetchReport(this.reportId, { password, fromUnlock: true })
    },
  },
}
</script>

<style scoped>
.predix-public-report {
  position: relative;
  color: #111827;
  background: #f3f4f6;
}

html.dark .predix-public-report {
  color: #f3f4f6;
  background: #030712;
}

.predix-public-report__bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(900px 420px at 8% -10%, color-mix(in srgb, var(--v-primary-base, #0393a8) 22%, transparent), transparent 60%),
    radial-gradient(700px 360px at 100% 0%, color-mix(in srgb, var(--v-primary-base, #0393a8) 10%, transparent), transparent 55%);
}

.predix-public-report__inner {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 78rem;
  margin: 0 auto;
  padding: 2rem 1rem 3rem;
  box-sizing: border-box;
}

@media (min-width: 640px) {
  .predix-public-report__inner {
    padding: 2.5rem 1.5rem 3.5rem;
  }
}

.predix-public-report__layout {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1.25rem;
  width: 100%;
}

@media (min-width: 960px) {
  .predix-public-report__layout {
    flex-direction: row;
    align-items: flex-start;
    gap: 1.5rem;
  }
}

.predix-public-report__sidebar {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  min-width: 0;
}

@media (min-width: 960px) {
  .predix-public-report__sidebar {
    flex: 1 1 0;
    width: 50%;
    max-width: calc(50% - 0.75rem);
    position: sticky;
    top: 1.25rem;
    align-self: flex-start;
    overflow: visible;
  }
}

.predix-public-report__content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
  flex: 1 1 0;
  width: 100%;
}

@media (min-width: 960px) {
  .predix-public-report__content {
    width: 50%;
  }
}

.predix-public-report__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 0;
  padding: 1.15rem 1.25rem;
  border-radius: 1rem;
  border: 1px solid rgb(229 231 235);
  background: #fff;
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.04);
}

html.dark .predix-public-report__header {
  border-color: rgb(255 255 255 / 0.1);
  background: rgb(17 24 39);
  box-shadow: none;
}

.predix-public-report__brand {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
}

.predix-public-report__brand-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  line-height: 1.2;
}

.predix-public-report__logo {
  display: block;
  height: 2.5rem;
  width: auto;
  flex-shrink: 0;
  object-fit: contain;
  align-self: center;
}

/* 200×48 EGA mark — same height as default mark, wider naturally */
.predix-public-report__logo--ega {
  height: 2.5rem;
  width: auto;
  max-width: 10rem;
}

.predix-public-report__eyebrow {
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  color: #6b7280;
  line-height: 1.3;
  white-space: nowrap;
  margin-top: 0.2rem;
}

html.dark .predix-public-report__eyebrow {
  color: #9ca3af;
}

.predix-public-report__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.3;
  word-break: break-word;
}

.predix-public-report__pdf-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  flex: 0 0 auto;
  margin-top: 0;
  padding: 0.7rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid color-mix(in srgb, var(--v-primary-base, #0393a8) 35%, #d1d5db);
  background: var(--v-primary-base, #0393a8);
  color: #fff;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.predix-public-report__pdf-btn:hover:not(:disabled) {
  filter: brightness(0.96);
  background: var(--v-primary-base, #0393a8);
}

.predix-public-report__pdf-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

html.dark .predix-public-report__pdf-btn {
  border-color: color-mix(in srgb, var(--v-primary-base, #0393a8) 45%, transparent);
  background: var(--v-primary-base, #0393a8);
  color: #fff;
}

.predix-public-report__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  min-height: 12rem;
  padding: 2rem 1rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.9375rem;
}

html.dark .predix-public-report__state {
  color: #9ca3af;
}

.predix-public-report__state--error {
  color: #b91c1c;
}

html.dark .predix-public-report__state--error {
  color: #f87171;
}

.predix-public-report__state-icon {
  font-size: 1.75rem;
  opacity: 0.85;
}

.predix-public-report__spinner {
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  border: 2px solid rgb(229 231 235);
  border-top-color: var(--v-primary-base, #0393a8);
  animation: predix-public-report-spin 0.7s linear infinite;
}

html.dark .predix-public-report__spinner {
  border-color: rgb(255 255 255 / 0.15);
  border-top-color: var(--v-primary-base, #0393a8);
}

@keyframes predix-public-report-spin {
  to {
    transform: rotate(360deg);
  }
}

.predix-public-report__unlock {
  max-width: 24rem;
  margin: 3rem auto 0;
  padding: 1.75rem 1.5rem;
  border-radius: 1rem;
  border: 1px solid #e5e7eb;
  background: rgb(255 255 255 / 0.92);
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.04);
  text-align: center;
}

html.dark .predix-public-report__unlock {
  border-color: rgb(255 255 255 / 0.1);
  background: rgb(17 24 39 / 0.72);
  box-shadow: none;
}

.predix-public-report__unlock-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  margin-bottom: 0.85rem;
  border-radius: 9999px;
  background: color-mix(in srgb, var(--v-primary-base, #0393a8) 14%, transparent);
  color: var(--v-primary-base, #0393a8);
  font-size: 1.1rem;
}

.predix-public-report__unlock-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
}

.predix-public-report__unlock-text {
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.45;
}

html.dark .predix-public-report__unlock-text {
  color: #9ca3af;
}

.predix-public-report__unlock-form {
  margin-top: 1.25rem;
  text-align: left;
}

.predix-public-report__unlock-label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #374151;
}

html.dark .predix-public-report__unlock-label {
  color: #d1d5db;
}

.predix-public-report__unlock-input {
  box-sizing: border-box;
  width: 100%;
  padding: 0.6rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #111827;
  font: inherit;
  font-size: 0.875rem;
}

.predix-public-report__unlock-input:focus {
  outline: none;
  border-color: color-mix(in srgb, var(--v-primary-base, #0393a8) 55%, #d1d5db);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--v-primary-base, #0393a8) 18%, transparent);
}

.predix-public-report__unlock-input--error {
  border-color: #f87171;
}

html.dark .predix-public-report__unlock-input {
  border-color: rgb(255 255 255 / 0.15);
  background: rgb(17 24 39 / 0.55);
  color: #f3f4f6;
}

.predix-public-report__unlock-error {
  margin: 0.45rem 0 0;
  font-size: 0.8125rem;
  color: #dc2626;
}

html.dark .predix-public-report__unlock-error {
  color: #f87171;
}

.predix-public-report__unlock-btn {
  box-sizing: border-box;
  width: 100%;
  margin-top: 0.85rem;
  padding: 0.65rem 1rem;
  border: none;
  border-radius: 0.5rem;
  background: var(--v-primary-base, #0393a8);
  color: #fff;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.predix-public-report__unlock-btn:hover:not(:disabled) {
  filter: brightness(0.96);
}

.predix-public-report__unlock-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.predix-public-report__cat {
  overflow: hidden;
  border-radius: 1rem;
  border: 1px solid #e5e7eb;
  background: #fff;
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.04);
}

html.dark .predix-public-report__cat {
  border-color: rgb(255 255 255 / 0.1);
  background: rgb(255 255 255 / 0.04);
  box-shadow: none;
}

.predix-public-report__cat-head {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.95rem 1.15rem;
  border-bottom: 1px solid #f3f4f6;
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--v-primary-base, #0393a8) 8%, #fff),
    #fff 55%
  );
}

html.dark .predix-public-report__cat-head {
  border-bottom-color: rgb(255 255 255 / 0.08);
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--v-primary-base, #0393a8) 16%, transparent),
    transparent 60%
  );
}

.predix-public-report__cat-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 650;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.predix-public-report__table-wrap {
  overflow-x: auto;
}

.predix-public-report__table {
  width: 100%;
  min-width: 240px;
  border-collapse: collapse;
  text-align: left;
  font-size: 0.875rem;
}

.predix-public-report__table thead tr {
  border-bottom: 1px solid #f3f4f6;
  background: #f9fafb;
  color: #6b7280;
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

html.dark .predix-public-report__table thead tr {
  border-bottom-color: rgb(255 255 255 / 0.08);
  background: rgb(17 24 39 / 0.45);
  color: #9ca3af;
}

.predix-public-report__table th,
.predix-public-report__table td {
  padding: 0.7rem 1.15rem;
}

.predix-public-report__prop {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  max-width: 100%;
}

.predix-public-report__prop-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.predix-public-report__help {
  flex-shrink: 0;
}

.predix-public-report__help-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.375rem;
  height: 1.375rem;
  margin: 0;
  padding: 0;
  border-radius: 9999px;
  border: 1px solid rgba(209, 213, 219, 0.95);
  background-color: rgb(243, 244, 246);
  color: rgb(55, 65, 81);
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1;
  cursor: help;
  transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease;
}

.predix-public-report__help-btn:hover,
.predix-public-report__help:focus-within .predix-public-report__help-btn {
  background-color: rgb(229, 231, 235);
  border-color: rgba(3, 147, 168, 0.45);
  color: rgb(17, 24, 39);
}

html.dark .predix-public-report__help-btn {
  border-color: rgba(255, 255, 255, 0.12);
  background-color: rgba(255, 255, 255, 0.06);
  color: rgb(209, 213, 219);
}

html.dark .predix-public-report__help-btn:hover,
html.dark .predix-public-report__help:focus-within .predix-public-report__help-btn {
  background-color: rgba(255, 255, 255, 0.12);
  border-color: rgba(3, 147, 168, 0.55);
  color: rgb(243, 244, 246);
}

.predix-public-report__help-tip {
  position: fixed;
  z-index: 4000;
  transform: translateY(-50%);
  width: min(16.5rem, 60vw);
  max-width: calc(100vw - 1.5rem);
  padding: 0.75rem 0.875rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(229, 231, 235, 0.95);
  background-color: rgba(255, 255, 255, 0.98);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 4px 6px -4px rgba(0, 0, 0, 0.08);
  text-align: left;
  pointer-events: none;
}

html.dark .predix-public-report__help-tip {
  border-color: rgba(255, 255, 255, 0.12);
  background-color: rgba(31, 41, 55, 0.98);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.45);
}

.predix-public-report__help-tip-title {
  margin-bottom: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: rgb(17, 24, 39);
}

html.dark .predix-public-report__help-tip-title {
  color: rgb(243, 244, 246);
}

.predix-public-report__help-tip-body {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.45;
  color: rgb(75, 85, 99);
  white-space: normal;
}

html.dark .predix-public-report__help-tip-body {
  color: rgb(156, 163, 175);
}

.predix-public-report__table tbody tr {
  border-bottom: 1px solid #f3f4f6;
}

.predix-public-report__table tbody tr:last-child {
  border-bottom: none;
}

html.dark .predix-public-report__table tbody tr {
  border-bottom-color: rgb(255 255 255 / 0.06);
}

.predix-public-report__table tbody tr:nth-child(even) {
  background: #fafafa;
}

html.dark .predix-public-report__table tbody tr:nth-child(even) {
  background: rgb(255 255 255 / 0.02);
}

.predix-public-report__col-value {
  text-align: right;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.predix-public-report__col-value--copyable {
  cursor: pointer;
  user-select: none;
  border-radius: 0.35rem;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.predix-public-report__col-value--copyable:hover {
  background: color-mix(in srgb, var(--v-primary-base, #0393a8) 12%, transparent);
  color: var(--v-primary-base, #0393a8);
}

.predix-public-report__col-value--copyable.is-copied {
  background: color-mix(in srgb, var(--v-primary-base, #0393a8) 18%, transparent);
  color: var(--v-primary-base, #0393a8);
}
</style>
